import React from 'react';
import _get from 'lodash/get';
import { useHistory } from 'react-router-dom';
import { Box, Button, makeStyles, Typography } from '@material-ui/core';
import Mixpanel from 'utils/mixpanelService';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { getImageFile, getTrackingPageName } from 'utils/stringUtils';
import { mapPracticeMethodToIcon } from 'containers/Practices/constants';
import Badge from '../Badge';
import BackgroundIcon from './icons/BackgroundIcon.svg';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'start',
    width: '100%',
    height: props => (props.resourceCard ? 271 : 285),
    overflow: 'hidden',
    padding: 14,
    borderRadius: 8,
    cursor: 'pointer',
    boxShadow: '10px 10px 24px 0px rgba(1, 97, 155, 0.05)',
    background: props =>
      !props.cardColor
        ? 'linear-gradient(223deg, #E8FAFF, #D3ECFF)'
        : `linear-gradient(to left, ${props.cardColor} 80%, ${theme.palette.text.white} 140%)`,
  },
  background: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    opacity: 0.3,
  },
  imageContainer: {
    width: 80,
    height: 80,
    margin: '16px auto',
    borderRadius: 10,
    overflow: 'hidden',

    '& > img': {
      zIndex: 0,
      maxWidth: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'flex-start',
    width: '100%',
  },
  methodsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),

    '& svg': {
      color: theme.palette.primary.main,
    },
  },
  title: {
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    zIndex: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: 4,
  },
  timeLabel: {
    fontSize: 12,
  },
  badgeContainer: {
    '& img': {
      width: 12,
      height: 12,
    },
  },
  button: {
    borderRadius: 5,
    marginBottom: 6,
    marginTop: 'auto',
    ...theme.typography.button,
  },
}));

const PracticeItem = ({ item, resourceCard, trackingProps = {} }) => {
  const classes = useStyles({ cardColor: item.cardColor, resourceCard });
  const history = useHistory();

  const [practicesPageSiteCopy, resourceItemPageSiteCopy] = useSiteCopySelector(
    ['practices', 'resource-item-page'],
  );

  const image = getImageFile(item);
  const methods = _get(item, 'methods', []);
  const timeLabel =
    resourceItemPageSiteCopy?.pageCopy?.durationLabelShortSymbol;

  const handleClick = () => {
    history.push(`/practices/${item.slug}`);
    const pageName = getTrackingPageName();
    Mixpanel.track('Resource Card - Clicked', {
      page: pageName,
      name: item.name,
      type: 'practice',
      ...trackingProps,
    });
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <Box className={classes.root} onClick={handleClick}>
      <img className={classes.background} alt="" src={BackgroundIcon} />
      <Box className={classes.header}>
        <Box display="flex" justifyContent="space-between">
          <Badge customClasses={classes} removeMargin type="practice" />
        </Box>
        <Box className={classes.methodsContainer}>
          {item?.showTime && (
            <Typography
              className={classes.timeLabel}
              color="primary"
            >{`${item.time}${timeLabel}`}</Typography>
          )}
          {mapPracticeMethodToIcon[(methods?.[0])]?.()}
        </Box>
      </Box>
      <Box className={classes.imageContainer}>
        <img src={image} alt="Practice" />
      </Box>
      <Typography
        variant="h6"
        color="textPrimary"
        align="center"
        className={classes.title}
      >
        {item.name}
      </Typography>
      <Button
        className={classes.button}
        variant="contained"
        color="primary"
        aria-label={`${item.name} ${_get(
          practicesPageSiteCopy,
          'pageCopy.startPractice',
        )}`}
      >
        {_get(practicesPageSiteCopy, 'pageCopy.startPractice')}
      </Button>
    </Box>
  );
};

export default PracticeItem;
